import {
  type ClipboardEvent,
  type KeyboardEvent,
  useId,
  useRef,
} from "react";
import { cn } from "@/lib/cn";

export interface OTPInputProps {
  /** Controlled value (the joined digits). */
  value: string;
  onChange: (value: string) => void;
  /** Number of cells. Defaults to 6. */
  length?: number;
  disabled?: boolean;
  /** Render the error (danger) border state. */
  error?: boolean;
  /** Auto-focus the first cell on mount. */
  autoFocus?: boolean;
  /** Fired when all cells are filled. */
  onComplete?: (value: string) => void;
  className?: string;
  /** Accessible group label. */
  ariaLabel?: string;
}

/**
 * OTPInput — segmented one-time-code field (DESIGN-SYSTEM §5).
 * Auto-advances on entry, supports backspace navigation and paste. Controlled
 * via `value`/`onChange`. Used on the Login TOTP step and Setup2fa.
 */
export function OTPInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  error = false,
  autoFocus = false,
  onComplete,
  className,
  ariaLabel = "Код подтверждения",
}: OTPInputProps) {
  const groupId = useId();
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const chars = Array.from({ length }, (_, i) => value[i] ?? "");

  function commit(next: string) {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  }

  function focusCell(i: number) {
    refs.current[i]?.focus();
    refs.current[i]?.select();
  }

  function handleCellChange(index: number, raw: string) {
    const digit = raw.replace(/\D/g, "");
    if (!digit) return;
    // Take the last typed character (handles overwrite).
    const arr = chars.slice();
    arr[index] = digit[digit.length - 1];
    const next = arr.join("").slice(0, length);
    commit(next);
    if (index < length - 1) focusCell(index + 1);
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = chars.slice();
      if (arr[index]) {
        arr[index] = "";
        commit(arr.join(""));
      } else if (index > 0) {
        arr[index - 1] = "";
        commit(arr.join(""));
        focusCell(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusCell(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusCell(index + 1);
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    commit(pasted);
    focusCell(Math.min(pasted.length, length - 1));
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex items-center gap-2", className)}
    >
      {chars.map((char, i) => (
        <input
          key={`${groupId}-${i}`}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={char}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`Цифра ${i + 1}`}
          onChange={(e) => handleCellChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            "h-12 w-10 rounded-lg border bg-bg-secondary text-center font-mono text-lg text-fg-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error ? "border-danger" : "border-border",
          )}
        />
      ))}
    </div>
  );
}
OTPInput.displayName = "OTPInput";
